import React, { useState } from "react";
import { FileSearch, MapPin, FileCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: 1,
  number: "01",
  title: "Nos compartes tu caso para apostilla de documentos",
  description: "Nos dices qué documento necesitas y en qué país lo vas a presentar; evaluamos tu apostilla y si ya cuentas con el documento o requerimos realizar trámites de apostilla desde cero en México.",
  icon: FileSearch
}, {
  id: 2,
  number: "02",
  title: "Revisamos requisitos y gestión de apostilla federal",
  description: "Confirmamos lo necesario para tu apostilla o trámite documental, revisamos si requiere apostilla federal y avanzamos con los trámites de apostilla de forma clara y ordenada.",
  icon: MapPin
}, {
  id: 3,
  number: "03",
  title: "Apostillamos y enviamos trámites internacionales",
  description: "Una vez finalizada la apostilla, enviamos tus documentos con seguimiento internacional para que recibas tus trámites de apostilla en España, Estados Unidos u otro país.",
  icon: FileCheck
}];
export default function AuditProcess() {
  const [activeStep, setActiveStep] = useState<number>(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="16035" id="apostillas" ref={ref} className="relative w-full py-20 lg:py-32 bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-lg blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-secondary/5 rounded-lg blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-3xl md:text-4xl font-default font-bold mb-6 tracking-tight">
            Nuestro proceso de apostillas y gestión de documentos mexicanos
          </h2>
          <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "Proceso de apostillas y gestión de documentos mexicanos",
            "description": "Pasos para apostillar y gestionar documentos oficiales de México desde el extranjero.",
            "step": steps.map(step => ({
              "@type": "HowStep",
              "name": step.title,
              "itemListElement": [{
                "@type": "HowToDirection",
                "text": step.description
              }]
            }))
          })
        }} />
          <p className="text-lg text-muted-foreground font-default leading-relaxed">
            Te guiamos paso a paso para apostillar y gestionar documentos mexicanos desde el extranjero, con revisión previa, seguimiento y envío internacional.
          </p>
        </div>

        {}
        <div className="relative max-w-6xl mx-auto">
          {}
          <div className="hidden lg:block absolute top-12 left-0 w-full h-[2px] bg-border">
            <div className="h-full bg-primary transition-all duration-500 ease-out" style={{
            width: `${activeStep / (steps.length - 1) * 100}%`
          }} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {steps.map((step, index) => {
            const isActive = index === activeStep;
            const Icon = step.icon;
            return <div key={step.id} data-index={index} onClick={() => setActiveStep(index)} className={cn("group relative flex flex-col p-8 rounded-2xl transition-all duration-500 cursor-pointer border", isActive ? "bg-accent/50 backdrop-blur-sm border-primary shadow-md translate-y-[-8px]" : "bg-card hover:bg-accent/30 border-border hover:border-primary/30", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
              transitionDelay: `${index * 150}ms`
            }}>
                  {}
                  <div className="flex items-center justify-between mb-6">
                    <span className={cn("font-mono text-4xl font-bold transition-colors duration-300", isActive ? "text-primary" : "text-muted-foreground/30 group-hover:text-muted-foreground/50")}>
                      {step.number}
                    </span>
                    <div className={cn("p-3 rounded-full transition-all duration-300", isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                      <Icon strokeWidth={1.5} className="w-6 h-6" />
                    </div>
                  </div>

                  {}
                  <h3 className={cn("text-xl font-default font-semibold mb-3 transition-colors duration-300", isActive ? "text-foreground" : "text-foreground/90")}>
                    {step.title}
                  </h3>
                  <p className={cn("text-sm leading-relaxed font-default transition-colors duration-300", isActive ? "text-foreground/90" : "text-muted-foreground")}>
                    {step.description}
                  </p>

                  {}
                  <div className={cn("absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-l-2xl lg:hidden transition-opacity duration-300", isActive ? "opacity-100" : "opacity-0")} />
                </div>;
          })}
          </div>
        </div>

        {}
        <div className={`mt-12 text-center transition-all duration-700 delay-400 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <p className="text-lg text-muted-foreground font-default italic">
            Desde el inicio te decimos qué sí procede, qué hace falta y qué sigue.
          </p>
        </div>

        {}
        <div className={`mt-16 text-center transition-all duration-700 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Button asChild variant="secondary" size="lg" className="font-default font-semibold text-secondary-foreground bg-secondary hover:bg-secondary/90 shadow-md active:scale-95 transition-transform active:shadow-lg">
            <Link to="https://wa.me/message/GPXE5OUSFQOBF1" newTab>
              Inicie su trámite
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
          <p className="mt-4 text-sm text-muted-foreground font-default">
            Respuesta en menos de 24 horas hábiles
          </p>
        </div>
      </div>
    </section>;
}